import { FEEDBACK_URL } from './endpoints';

/**
 * Sending one message from the drop box (GDD §13).
 *
 * Sibling of `field.ts` and deliberately its opposite in one respect: the field
 * readout fails open, because a counter that does not move is nobody's
 * business. Somebody is watching this one. A player who typed a paragraph and
 * pressed send is owed an answer either way, so every path here resolves to a
 * word the panel has copy for and nothing is ever swallowed into silence.
 *
 * It is not gated on the usage breaker. That switch governs collection the game
 * does in the background; this is an explicit act, and discarding a typed
 * message because of a preference about background telemetry would be the worst
 * outcome available.
 */

/** Longer than the field's three seconds: somebody is waiting on this one. */
const TIMEOUT_MS = 8000;

/** The kind chips, which are triage help and never a gate. */
export type FeedbackKind = 'bug' | 'idea' | 'grid' | 'other';

/** Matches TEXT_MAX in backend/feedback-plan.mjs. */
export const TEXT_MAX = 1000;
/** Matches CONTACT_MAX there too. */
export const CONTACT_MAX = 120;

/**
 * What rides along with the message, and the whole of it. The panel prints
 * these three before the send, so the list is checkable rather than promised.
 */
export interface FeedbackContext {
  /** Absent in practice and in a drill, neither of which has a grid number. */
  grid?: number;
  mode: 'daily' | 'archive' | 'practice' | 'drill';
  build: string;
}

/**
 * How it went, in the four words the panel can speak. `offline` covers every
 * "not your fault, try later": a dead network, a 5xx, and the deployed-but-
 * unconfigured 503 all read the same from where the player sits.
 */
export type FeedbackOutcome = 'ok' | 'throttled' | 'invalid' | 'offline';

/** Whether this build has a drop box behind it at all. */
export function feedbackEnabled(): boolean {
  return FEEDBACK_URL !== null;
}

export interface FeedbackMessage {
  kind: FeedbackKind;
  text: string;
  contact: string;
  ctx: FeedbackContext;
}

/** Never throws and never rejects: every outcome is one of the four words. */
export async function sendFeedback(msg: FeedbackMessage): Promise<FeedbackOutcome> {
  if (FEEDBACK_URL === null) return 'offline';
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(FEEDBACK_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        v: 1,
        kind: msg.kind,
        text: msg.text,
        contact: msg.contact || null,
        ctx: msg.ctx,
      }),
      // No keepalive, unlike the event beacons: the panel is on screen waiting
      // for this answer, so the page is not going anywhere.
      signal: controller.signal,
      cache: 'no-store',
    });
    if (res.ok) return 'ok';
    if (res.status === 429) return 'throttled';
    if (res.status === 400) return 'invalid';
    return 'offline';
  } catch {
    return 'offline';
  } finally {
    clearTimeout(timer);
  }
}
